import {
  Activity,
  AppWindow,
  BadgeCheck,
  Boxes,
  Building2,
  Code2,
  Cpu,
  DatabaseZap,
  GraduationCap,
  HeartPulse,
  Layers3,
  LucideIcon,
  MapPinned,
  Paintbrush,
  PlugZap,
  Rocket,
  ServerCog,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Store,
  Truck,
  WalletCards,
  Workflow,
} from "lucide-react";

export const logoPath = "/assets/lazerwebs-logo.jpg";

export const officialEmail = "support@lazerwebs.com";
export const officialPhone = "+2349033572505";
export const officialWhatsAppNumber = "2349033572505";
export const officialWhatsAppUrl = `https://wa.me/${officialWhatsAppNumber}`;

export const navItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Products", href: "/products" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

export type IconItem = {
  title: string;
  description: string;
  icon: LucideIcon;
};

export const trustBadges = [
  "Websites",
  "Mobile Apps",
  "SaaS Platforms",
  "Enterprise Software",
  "VTU Solutions",
  "E-commerce Systems",
];

export const services: IconItem[] = [
  {
    title: "Website Development",
    description:
      "Conversion-focused corporate websites, portals, landing pages, and web apps built for speed, SEO, and credibility.",
    icon: AppWindow,
  },
  {
    title: "Mobile App Development",
    description:
      "Native-quality iOS and Android applications with clean interfaces, robust APIs, and reliable release support.",
    icon: Smartphone,
  },
  {
    title: "SaaS Development",
    description:
      "Scalable subscription platforms with dashboards, billing flows, roles, analytics, and secure cloud architecture.",
    icon: Layers3,
  },
  {
    title: "Custom Software Development",
    description:
      "Tailored business applications that automate workflows, reduce manual effort, and connect teams to better data.",
    icon: Code2,
  },
  {
    title: "E-commerce Development",
    description:
      "Modern storefronts, marketplaces, order management, payments, inventory, and mobile shopping experiences.",
    icon: ShoppingCart,
  },
  {
    title: "VTU Platform Development",
    description:
      "Reliable vending systems for airtime, data, bills, wallets, agents, commissions, and mobile-first operations.",
    icon: WalletCards,
  },
  {
    title: "School Management Systems",
    description:
      "Digital tools for admissions, fees, attendance, exams, results, parents, teachers, and administration.",
    icon: GraduationCap,
  },
  {
    title: "Hospital Management Systems",
    description:
      "Patient records, appointments, billing, pharmacy, diagnostics, staff workflows, and operational reporting.",
    icon: HeartPulse,
  },
  {
    title: "POS & Retail Systems",
    description:
      "Point-of-sale software, stock control, receipts, branch management, analytics, and multi-user access.",
    icon: Store,
  },
  {
    title: "Logistics & Delivery Apps",
    description:
      "Fleet, rider, dispatch, tracking, proof-of-delivery, payment, and customer communication systems.",
    icon: Truck,
  },
  {
    title: "UI/UX Design",
    description:
      "Product strategy, wireframes, prototypes, design systems, and polished user experiences for every screen.",
    icon: Paintbrush,
  },
  {
    title: "API Development & Integration",
    description:
      "Secure integrations for payments, messaging, maps, identity, ERP systems, CRMs, and third-party platforms.",
    icon: PlugZap,
  },
];

export type Product = {
  name: string;
  description: string;
  features: string[];
  icon: LucideIcon;
};

export type PortfolioProject = {
  industry: string;
  title: string;
  description: string;
  icon: LucideIcon;
  sample?: {
    label: string;
    url: string;
  };
  image: {
    src: string;
    alt: string;
  };
};

export const products: Product[] = [
  {
    name: "VTU Website & Mobile App",
    description:
      "A complete vending ecosystem for airtime, data, bills, wallets, agents, and automated transactions.",
    features: [
      "Wallet system",
      "Agent dashboard",
      "Bill payments",
      "Mobile-ready UX",
    ],
    icon: WalletCards,
  },
  {
    name: "E-commerce Website & Mobile App",
    description:
      "A polished online store with product management, checkout, payments, logistics, and customer accounts.",
    features: [
      "Product catalog",
      "Secure checkout",
      "Order tracking",
      "Inventory tools",
    ],
    icon: ShoppingCart,
  },
  {
    name: "School Management System",
    description:
      "A centralized academic platform for admissions, fees, attendance, exams, results, and parent access.",
    features: [
      "Student records",
      "Fee management",
      "Result portal",
      "Parent access",
    ],
    icon: GraduationCap,
  },
  {
    name: "Hospital Management System",
    description:
      "A healthcare operations suite for patient records, appointments, billing, pharmacy, and reports.",
    features: ["EMR records", "Appointments", "Billing", "Pharmacy module"],
    icon: HeartPulse,
  },
  {
    name: "POS System",
    description:
      "A retail-ready platform for sales, receipts, branch reporting, stock control, and staff management.",
    features: [
      "Sales terminal",
      "Stock control",
      "Receipts",
      "Branch analytics",
    ],
    icon: Store,
  },
  {
    name: "Logistics & Delivery Platform",
    description:
      "Dispatch, rider, customer, and admin tools for delivery businesses that need real-time visibility.",
    features: [
      "Live tracking",
      "Rider app",
      "Dispatch panel",
      "Proof of delivery",
    ],
    icon: Truck,
  },
  {
    name: "Real Estate Platform",
    description:
      "Property listings, agent dashboards, inquiry management, media galleries, and lead capture tools.",
    features: ["Listings", "Agent panel", "Lead capture", "Media galleries"],
    icon: Building2,
  },
  {
    name: "Company Website Package",
    description:
      "A premium digital presence for businesses that need speed, clarity, trust, and polished branding.",
    features: [
      "Responsive pages",
      "SEO structure",
      "Contact forms",
      "Fast launch",
    ],
    icon: AppWindow,
  },
  {
    name: "Custom SaaS Platform",
    description:
      "A tailored SaaS product built around your users, workflows, revenue model, and operational needs.",
    features: ["User roles", "Subscriptions", "Dashboards", "Cloud deployment"],
    icon: ServerCog,
  },
];

export const process = [
  {
    title: "Discover",
    description:
      "We map your goals, users, workflows, integrations, risks, and launch expectations.",
  },
  {
    title: "Design",
    description:
      "We shape elegant journeys, prototypes, interface systems, and product architecture.",
  },
  {
    title: "Develop",
    description:
      "We build secure, scalable software with clear milestones and practical communication.",
  },
  {
    title: "Deploy",
    description:
      "We launch with performance, analytics, SEO, infrastructure, and release readiness in mind.",
  },
  {
    title: "Support",
    description:
      "We help maintain, optimize, scale, and improve your solution after launch.",
  },
];

export const portfolio: PortfolioProject[] = [
  {
    industry: "Fintech",
    title: "Digital wallet and VTU transaction platform",
    description:
      "High-volume vending, wallet reconciliation, agent commissions, and admin analytics.",
    icon: WalletCards,
    sample: {
      label: "Subbnow VTU sample",
      url: "https://subbnow.com",
    },
    image: {
      src: "/assets/projects/fintech-vtu-platform.png",
      alt: "Responsive dark-mode fintech wallet and VTU platform interface mockups",
    },
  },
  {
    industry: "E-commerce",
    title: "Multi-vendor commerce ecosystem",
    description:
      "Storefront, seller tools, inventory, payment flows, delivery tracking, and reporting.",
    icon: ShoppingCart,
    sample: {
      label: "Abusudais Tech e-commerce sample",
      url: "https://abusudaistech.com.ng",
    },
    image: {
      src: "/assets/projects/ecommerce-platform.png",
      alt: "Responsive e-commerce dashboard and mobile shopping app interface mockups",
    },
  },
  {
    industry: "Education",
    title: "School operations suite",
    description:
      "Academic records, exams, fees, parents, teachers, and management in one platform.",
    icon: GraduationCap,
    image: {
      src: "/assets/projects/education-management-system.png",
      alt: "Responsive school management system dashboard interface mockups",
    },
  },
  {
    industry: "Healthcare",
    title: "Hospital workflow platform",
    description:
      "Patient visits, appointments, pharmacy, billing, diagnostics, and role-based access.",
    icon: HeartPulse,
    image: {
      src: "/assets/projects/healthcare-workflow-platform.png",
      alt: "Responsive hospital management workflow dashboard interface mockups",
    },
  },
  {
    industry: "Logistics",
    title: "Delivery and dispatch command center",
    description:
      "Fleet visibility, rider assignment, customer updates, and proof-of-delivery workflows.",
    icon: Truck,
    image: {
      src: "/assets/projects/logistics-delivery-platform.png",
      alt: "Responsive logistics delivery and dispatch platform interface mockups",
    },
  },
  {
    industry: "Agriculture",
    title: "Supply chain management portal",
    description:
      "Producer onboarding, commodity tracking, procurement, inventory, and reporting.",
    icon: Boxes,
    image: {
      src: "/assets/projects/agriculture-supply-chain.png",
      alt: "Responsive agritech supply chain management portal interface mockups",
    },
  },
  {
    industry: "Government",
    title: "Citizen service and records system",
    description:
      "Secure public-sector forms, case routing, identity-aware workflows, and dashboards.",
    icon: ShieldCheck,
    image: {
      src: "/assets/projects/government-citizen-services.png",
      alt: "Responsive government citizen services and records system interface mockups",
    },
  },
  {
    industry: "Enterprise Software",
    title: "Internal automation platform",
    description:
      "Approvals, team workflows, reporting, integrations, and operational visibility.",
    icon: Workflow,
    image: {
      src: "/assets/projects/enterprise-automation-platform.png",
      alt: "Responsive enterprise automation and workflow dashboard interface mockups",
    },
  },
];

export const sampleProjects = [
  {
    name: "Subbnow",
    category: "VTU platform",
    url: "https://subbnow.com",
  },
  {
    name: "Abusudais Tech",
    category: "E-commerce platform",
    url: "https://abusudaistech.com.ng",
  },
  {
    name: "GameHub",
    category: "Gaming and digital product platform",
    url: null,
  },
  {
    name: "Implexa",
    category: "Company and digital platform",
    url: "https://implexa.org",
  },
  {
    name: "SPHCDA Kwara",
    category: "Government and public-sector platform",
    url: "https://sphcda.kw.gov.ng",
  },
];

export const pricing = [
  {
    name: "Starter Website Package",
    description:
      "A refined digital presence for new businesses, portfolios, and service brands.",
    features: [
      "Up to 5 core pages",
      "Responsive design",
      "Basic SEO setup",
      "Contact form",
    ],
  },
  {
    name: "Business Website Package",
    description:
      "A premium company website with richer content, stronger conversion flows, and analytics.",
    features: [
      "Custom page sections",
      "CMS-ready structure",
      "Analytics setup",
      "Speed optimization",
    ],
  },
  {
    name: "E-commerce Package",
    description:
      "A complete online selling experience for products, orders, payments, and customers.",
    features: [
      "Product catalog",
      "Cart and checkout",
      "Payment integration",
      "Order management",
    ],
  },
  {
    name: "VTU Website Package",
    description:
      "A ready-to-launch VTU business platform with wallet, bill payment, and admin controls.",
    features: [
      "Wallet funding",
      "Service vending",
      "Agent roles",
      "Transaction reports",
    ],
  },
  {
    name: "Mobile App Package",
    description:
      "A sleek mobile application for iOS and Android with backend and release support.",
    features: [
      "Android and iOS builds",
      "API integration",
      "Push notifications",
      "App store guidance",
    ],
  },
  {
    name: "Custom Software Package",
    description:
      "A tailored system for complex workflows, integrations, compliance, and scale.",
    features: [
      "Discovery workshop",
      "Custom architecture",
      "User roles",
      "Deployment support",
    ],
  },
];

export const values = [
  "Innovation",
  "Excellence",
  "Integrity",
  "Customer Success",
  "Quality",
  "Collaboration",
  "Continuous Improvement",
];

export const stats = [
  { value: "100%", label: "Customer-focused delivery" },
  { value: "24/7", label: "Digital-first business support" },
  { value: "230+", label: "Solution categories served" },
  { value: "5", label: "Launch phases refined" },
];

export const techStack = [
  "React",
  "TypeScript",
  "Node.js",
  "Laravel",
  "Flutter",
  "React Native",
  "PostgreSQL",
  "MySQL",
  "AWS",
  "Firebase",
  "Docker",
  "Payment APIs",
];

export const contactMethods = [
  {
    label: "WhatsApp",
    value: officialPhone,
    href: officialWhatsAppUrl,
    icon: Activity,
  },
  {
    label: "Email",
    value: officialEmail,
    href: `mailto:${officialEmail}`,
    icon: Sparkles,
  },
  {
    label: "Phone",
    value: officialPhone,
    href: `tel:${officialPhone}`,
    icon: Smartphone,
  },
  {
    label: "Office",
    value: "Nigeria, serving clients globally",
    href: "/contact",
    icon: MapPinned,
  },
];

export const differentiators: IconItem[] = [
  {
    title: "Enterprise-ready engineering",
    description:
      "Security, scalability, clean data models, and operational reliability are treated as first-class requirements.",
    icon: ShieldCheck,
  },
  {
    title: "Product thinking from day one",
    description:
      "We build around user journeys, business outcomes, launch timelines, and measurable adoption.",
    icon: Cpu,
  },
  {
    title: "Ready-made and custom options",
    description:
      "Move quickly with proven software packages or commission a fully tailored platform for your workflows.",
    icon: DatabaseZap,
  },
  {
    title: "A partner beyond launch",
    description:
      "Lazerwebs stays close after deployment with support, iteration, optimization, and growth guidance.",
    icon: BadgeCheck,
  },
];

export const socialLinks = ["LinkedIn", "X", "Instagram", "Facebook"];

export const mission =
  "To empower businesses, organizations, and governments with innovative, scalable, and reliable software solutions that drive digital transformation and sustainable growth.";

export const vision =
  "To become Africa's most trusted software development company, delivering world-class digital solutions that transform industries and improve lives globally.";

export const tagline = "Innovating the Future, One Solution at a Time.";

export const companyDescription =
  "Lazerwebs is a software development company building websites, mobile apps, SaaS platforms, enterprise software, and ready-made solutions for ambitious organizations.";

export const heroHeadline = "Build Powerful Digital Solutions With Lazerwebs";

export const heroSubheadline =
  "From websites and mobile apps to enterprise software and SaaS platforms, Lazerwebs delivers secure, scalable, and innovative digital solutions that help businesses grow, automate operations, and stay ahead of the competition.";

export const subsidiariesStatement =
  "Lazerwebs is a subsidiary of Freakyplus Technologies Limited.";

export const featuredProductNames = [
  "VTU Website & Mobile App",
  "E-commerce Website & Mobile App",
  "School Management System",
  "Hospital Management System",
  "POS System",
  "Logistics & Delivery Platform",
];

export const spotlightMetrics = [
  "Secure architecture",
  "Responsive delivery",
  "Fast deployment",
  "Scalable products",
];

export const heroCapabilities = [
  { label: "Web", icon: AppWindow },
  { label: "Mobile", icon: Smartphone },
  { label: "SaaS", icon: Layers3 },
  { label: "Enterprise", icon: ServerCog },
  { label: "Launch", icon: Rocket },
];
